import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { siteState, cameraState } from '../state/statistics.state';
import { GetSiteData, GetCameraData } from './statistics.hooks';

const INITIALIZATION_KEY = 'siteCameraInitialized';
let globalInitializationDone = false;

export function resetGlobalInitialization(): void {
  globalInitializationDone = false;
}

export function useInitializeSiteAndCamera() {
  const [site, setSite] = useRecoilState(siteState);
  const [camera, setCamera] = useRecoilState(cameraState);

  const { data: siteData, isSuccess: siteLoadSuccess } = GetSiteData();
  const { data: cameraData, isSuccess: cameraLoadSuccess } = GetCameraData(site);

  useEffect(() => {
    if (!siteLoadSuccess || !siteData?.data?.length) return;
    if (globalInitializationDone) return;

    const persistedSite = typeof window !== 'undefined' ? 
      (() => {
        try {
          const saved = window.localStorage.getItem('site');
          return saved ? JSON.parse(saved) : null;
        } catch {
          return null;
        }
      })() : null;

    if (persistedSite) {
      const isValidPersistedSite = siteData.data.some((s: any) => String(s.id) === persistedSite);
      if (isValidPersistedSite) {
        setSite((currentSite) => {
          return currentSite !== persistedSite ? persistedSite : currentSite;
        });
      } else if (siteData.data.length > 0) {
        setSite(String(siteData.data[0].id));
      }
      globalInitializationDone = true;
      return;
    }

    const hasInitialized = typeof window !== 'undefined' && 
      window.localStorage.getItem(INITIALIZATION_KEY) === 'true';

    if (!hasInitialized) {
      const defaultSite = String(siteData.data[0].id);
      setSite((currentSite) => {
        const currentSiteId = currentSite || '';
        const isValidSite = currentSiteId && siteData.data.some((s: any) => String(s.id) === currentSiteId);
        if (!isValidSite) {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(INITIALIZATION_KEY, 'true');
          }
          return defaultSite;
        }
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(INITIALIZATION_KEY, 'true');
        }
        return currentSite;
      });
    }

    globalInitializationDone = true;
  }, [siteLoadSuccess, siteData, setSite]);

  useEffect(() => {
    if (!site || !cameraLoadSuccess || !cameraData?.data?.length) return;

    const availableCameraIds = new Set(cameraData.data.map((cam: any) => cam.id));
    
    setCamera((currentCameras) => {
      const validCameras = currentCameras.filter((camId: number) => availableCameraIds.has(camId));

      if (currentCameras.length === 0) {
        return cameraData.data.map((cam: any) => cam.id);
      } else if (validCameras.length === 0) {
        return cameraData.data.map((cam: any) => cam.id);
      } else if (validCameras.length !== currentCameras.length) {
        return validCameras;
      }
      return currentCameras;
    });
  }, [site, cameraLoadSuccess, cameraData, setCamera]);

  return {
    site,
    camera,
    siteData,
    cameraData,
    siteLoadSuccess,
    cameraLoadSuccess
  };
}
